import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authAPI.login(formData);
      login(data.token, data.user);
      toast.success(t('auth.welcomeBack'));
      const target = location.state?.from?.pathname || (data.user.userType === 'admin' ? '/' : '/panel');
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#033b73',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <div
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          <LanguageSwitcher />
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        aria-live="polite"
        style={{
          backgroundColor: '#ffffff',
          padding: '36px 32px',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: '380px',
        }}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '18px',
            padding: '18px 30px',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '28px',
          }}
        >
          <img src="/logo.webp" alt="Logo" style={{ height: '56px' }} />
        </div>

        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#111827',
            textAlign: 'center',
            margin: 0,
          }}
        >
          {t('auth.systemTitle')}
        </h1>
        <p
          style={{
            fontSize: '14px',
            color: '#6b7280',
            textAlign: 'center',
            margin: '4px 0 0 0',
          }}
        >
          {t('auth.systemSubtitle')}
        </p>

        <div style={{ marginTop: '28px', position: 'relative' }}>
          <label
            htmlFor="username"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            {t('auth.username')}
          </label>
          <div style={{ position: 'relative' }}>
            <Mail
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
              size={16}
            />
            <input
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
              style={{
                width: '100%',
                height: '44px',
                paddingLeft: '40px',
                paddingRight: '12px',
                backgroundColor: '#eff6ff',
                border: '1px solid #dbeafe',
                borderRadius: '10px',
                fontSize: '14px',
                color: '#111827',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '14px', position: 'relative' }}>
          <label
            htmlFor="password"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            {t('auth.password')}
          </label>
          <div style={{ position: 'relative' }}>
            <Lock
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
              size={16}
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              style={{
                width: '100%',
                height: '44px',
                paddingLeft: '40px',
                paddingRight: '40px',
                backgroundColor: '#eff6ff',
                border: '1px solid #dbeafe',
                borderRadius: '10px',
                fontSize: '14px',
                color: '#111827',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            height: '46px',
            marginTop: '24px',
            backgroundColor: loading ? '#022a55' : '#033b73',
            color: 'white',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '10px',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'background-color 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>...</span>
            </>
          ) : (
            t('auth.login')
          )}
        </button>
      </form>
    </div>
  );
};

export default Login;
